import React, {
    FocusEvent,
    MouseEvent,
    useRef,
    useState,
    forwardRef,
    ReactNode,
    MutableRefObject,
    Children,
    useEffect,
    ReactElement,
    cloneElement,
    useImperativeHandle,
    ChangeEvent,
    useCallback,
} from 'react';
import { FlosInput, FlosInputProps } from '../input/flos-input';
import { Button, ButtonProps } from '../../button/button';
import { callAll } from '../../../utils/fp';
import cx from 'classnames';

export type Option = {
    value: string;
    disabled?: boolean;
};

export type TimeInputProps = {
    options?: Array<Option>;
    children?: ReactNode;
} & FlosInputProps;

const TimeInput = forwardRef<HTMLInputElement, TimeInputProps>(function TimeInputComp({ options, children, onChange, className, ...props }, forwardedRef) {
    const inputRef = useRef() as MutableRefObject<HTMLInputElement>;
    const [showOptions, setShowOptions] = useState(false);
    const [positionAbove, setPositionAbove] = useState(true);
    const [inputValue, setInputValue] = useState('');
    const [dialogPositionStyle, setDialogPositionStyle] = useState({});

    const dialogRef = useRef() as MutableRefObject<HTMLInputElement>;

    useImperativeHandle(forwardedRef, () => inputRef.current as HTMLInputElement);

    const childrenArray = Children.toArray(children);

    const handleInputFocus = () => {
        setShowOptions(true);
    };
    const dialogStyle = useCallback((elementHeight: number, showOptionsAbove: boolean) => {
        if (showOptionsAbove) {
            setDialogPositionStyle({
                bottom: `${elementHeight}px`,
            });
        } else {
            setDialogPositionStyle({});
        }
    }, []);

    const onFocusHandler = (event: FocusEvent<HTMLInputElement>) => {
        const element = event.currentTarget;
        if (element) {
            const elementRect = element.getBoundingClientRect();
            const spaceBelow = window.innerHeight - elementRect.bottom;
            const dialogHeight = element && element.parentElement ? element.parentElement.getElementsByClassName('timefield-dialog')[0].getBoundingClientRect().height : 0;
            if (dialogHeight > 0 && spaceBelow < dialogHeight + 5) {
                setPositionAbove(true);
                dialogStyle(elementRect.height, true);
            } else {
                setPositionAbove(false);
                dialogStyle(elementRect.height, false);
            }
        }
        setShowOptions(true);
        props.onFocus && props.onFocus(event);
    };

    const handleValueUpdate = (e: ChangeEvent<HTMLInputElement> | MouseEvent<HTMLButtonElement>) => {
        setInputValue(e.currentTarget.value);
        hide();
        if (onChange) onChange(e as ChangeEvent<HTMLInputElement>);
    };

    const hide = useCallback(() => {
        setTimeout(() => {
            setShowOptions(false);
            setPositionAbove(false);
            dialogStyle(0, true);
        }, 120);
    }, [dialogStyle]);
    const handleTab = useCallback(
        (e: globalThis.KeyboardEvent) => {
            if (!showOptions) return;
            // Gets all available timeslots
            const timeslots = dialogRef.current.querySelectorAll('.flos-button:not(.disabled)');

            const firstTimeslot = timeslots[0];
            const lastTimeslot = timeslots[timeslots.length - 1];

            if (!e.shiftKey && document.activeElement == lastTimeslot) {
                (firstTimeslot as HTMLElement).focus();
                return e.preventDefault();
            }
            if (e.shiftKey && document.activeElement == firstTimeslot) {
                (lastTimeslot as HTMLElement).focus();
                e.preventDefault();
            }
        },
        [showOptions]
    );
    const handleArrowKey = useCallback(
        (e: globalThis.KeyboardEvent, goingUp: boolean) => {
            if (!showOptions) return;
            const timeslots = dialogRef.current.querySelectorAll('.flos-button:not([disabled])');
            let currentIndex = -1;
            const firstTimeslot = timeslots[0];
            const lastTimeslot = timeslots[timeslots.length - 1];
            if (!goingUp && document.activeElement == lastTimeslot) {
                (firstTimeslot as HTMLElement).focus();
                return e.preventDefault();
            } else if (goingUp && document.activeElement == firstTimeslot) {
                (lastTimeslot as HTMLElement).focus();
                e.preventDefault();
            } else {
                timeslots.forEach((timeSlot, index) => {
                    if (document.activeElement == timeSlot) {
                        currentIndex = index;
                    }
                });
                if (currentIndex == -1) {
                    (firstTimeslot as HTMLElement).focus();
                } else {
                    if (goingUp) {
                        (timeslots[currentIndex - 1] as HTMLElement).focus();
                    } else {
                        (timeslots[currentIndex + 1] as HTMLElement).focus();
                    }
                }
                e.preventDefault();
            }
        },
        [showOptions]
    );

    useEffect(() => {
        const keyListener = (e: globalThis.KeyboardEvent) => {
            if (e.key == 'Tab') {
                handleTab(e);
            }
            if (e.key == 'Escape') {
                hide();
            }
            if (e.key == 'Enter' && !showOptions) {
                if (inputRef.current === e.target) {
                    setShowOptions(true);
                }
            }
            if (e.key == 'ArrowUp') {
                handleArrowKey(e, true);
            }
            if (e.key == 'ArrowDown') {
                handleArrowKey(e, false);
            }
        };
        const handleClickOutside = (e: globalThis.MouseEvent) => {
            if (!showOptions) return;
            if (!inputRef.current.contains(e.target as Node) && !dialogRef.current.contains(e.target as Node)) hide();
        };
        document.addEventListener('keydown', keyListener);
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('keydown', keyListener);
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showOptions, hide, handleArrowKey, handleTab]);
    return (
        <>
            <FlosInput
                autoComplete={'off'}
                type="text"
                role="combobox"
                aria-expanded={showOptions}
                value={inputValue}
                onFocusCapture={onFocusHandler}
                onClick={handleInputFocus}
                onChange={callAll(onChange, handleValueUpdate)}
                className={cx(className, { 'flos-input-dialog-below': showOptions && !positionAbove, 'flos-input-dialog-above': showOptions && positionAbove })}
                {...props}
                ref={inputRef}
            />
            <div
                style={dialogPositionStyle}
                className={cx('timefield-dialog', { visibilityHidden: !showOptions, 'timefield-dialog-above': positionAbove })}
                role="dialog"
                ref={dialogRef}
                aria-label="Vælg et tidspunkt"
                tabIndex={-1}
            >
                <div className="timefield-dialog-inner" tabIndex={-1}>
                    {options?.map((option: Option) => (
                        <Button
                            className={inputValue == option.value ? 'active' : ''}
                            key={option.value}
                            value={option.value}
                            onClick={handleValueUpdate}
                            theme="secondary"
                            disabled={option.disabled}
                        >
                            {option.value}
                        </Button>
                    ))}
                    {children &&
                        childrenArray.map((child: ReactElement<ButtonProps>) => {
                            return cloneElement(child, { onClick: handleValueUpdate, className: inputValue == child.props.value ? 'active' : '' });
                        })}
                </div>
            </div>
        </>
    );
});

export default TimeInput;
export { TimeInput };
